import React, {
	useEffect,
	useState
} from "react";
import { Inertia } from "@inertiajs/inertia";
import Modal from "@/components/Modal";
import ProductList from "@/components/layout/ProductList";
import OrderForm from "@/components/layout/OrderForm";
import Heading from "@/components/elements/Heading";
import api, { getHeaders } from "@/services/api";

/**
 * ShoppingList component that manages the shopping cart and order processing
 * Handles product display, customer information, delivery details, and order placement
 * Features admin-specific controls and comprehensive order validation
 *
 * @param {Object} props - Component props
 * @param {string} props.createNewProductLink - URL to create new products
 * @param {Array} props.products - Array of products in the shopping cart
 * @param {Object} props.contact - Customer contact and delivery information
 * @param {Function} props.updateProducts - Function to update products array
 * @param {Function} props.updateContact - Function to update contact information
 * @param {Object} props.session - Current session data
 * @param {Function} props.updateSession - Function to update session data
 * @param {string} props.saveOrder - API endpoint to save/submit orders
 * @param {Array} props.customers - Available customers (admin only)
 * @param {string} props.getContacts - API endpoint to get customer contacts
 */
const ShoppingList = ({
    createNewProductLink,
    products,
    contact,
    updateProducts,
    updateContact,
    session,
    updateSession,
    saveOrder,
    customers,
    getContacts,
}) => {

    // State management for UI and data
    const [thanks, setThanks] = useState(false); // Order success state
    const [modalopen, setModalOpen] = useState(false); // Product deletion modal
    const [selectedProductForDelete, setSelectedProductForDelete] = useState(null);
    const [errors, setErrors] = useState([]); // Form validation errors
    const [orderPopup, setOrderPopup] = useState(false); // Order confirmation modal
    const [showProductData, setShowProductData] = useState(false); // Product details modal
    const [orderPopupData, setOrderPopupData] = useState(""); // Order confirmation content
    const [orderPopupTitle, setOrderPopupTitle] = useState(""); // Order confirmation title
    const [productDataContent, setProductDataContent] = useState(""); // Product details content
    const [productDataTitle, setProductDataTitle] = useState(""); // Product details title
    const [contacts, setContacts] = useState([]); // Available contacts for selected customer

    /**
     * Load contacts for customer when contact.company_id changes
     */
    useEffect(() => {
        if (contact.company_id) {
            getContactsForCompany(contact.company_id);
        }
    }, []);

    /**
     * Show product deletion confirmation modal
     *
     * @param {Event} e - Click event
     * @param {Object} product - Product to delete
     * @param {number} key - Product index
     */
    function deleteProduct(e, product, key) {
        e.preventDefault();
        setSelectedProductForDelete(product);
        setModalOpen(true);
    }

    /**
     * Duplicate an existing product
     * Creates a copy via API call
     *
     * @param {Event} e - Click event
     * @param {Object} product - Product to duplicate
     * @param {number} key - Product index
     */
    function duplicateProduct(e, product, key) {
        Inertia.put(
            `/api/product/duplicate/${product.session_id}/${product.product_id}`,
	        null,
	        {headers: getHeaders()}
        );
    }

    /**
     * Actually delete the selected product
     * Removes from server and updates local state
     */
    function deleteProductDef() {
        api.delete(
            `/api/product/${selectedProductForDelete.session_id}/${selectedProductForDelete.product_id}`,
        ).then(() => {
            let prods = [...products];
            let index = prods.findIndex(
                (x) => x.product_id === selectedProductForDelete.product_id,
            );
            prods.splice(index, 1);
            updateProducts(prods);
        });
        setModalOpen(false);
    }

    /**
     * Close product details modal
     *
     * @param {Event} e - Click event (optional)
     */
    function closeProductData(e) {
        if (e) {
            e.preventDefault();
        }
        setShowProductData(false);
    }

    /**
     * Validate order form before submission
     * Checks required fields for order completion
     *
     * @returns {boolean} Whether validation passed
     */
    function checkErrors() {
        let errors = {};
        if (!session.description) {
            errors.description = "Er is geen referentie opgegeven";
        }
        if (!contact.address) {
            errors.address = "Er is geen adres ingevoerd";
        }
        if (!contact.delivery_date_preference) {
            errors.delivery_date_preference = "Er is geen afleverdatum ingevoerd";
        }
        if (!contact.city) {
            errors.city = "Er is geen plaats ingevoerd";
        }
        if (!contact.country) {
            errors.country = "Er is geen land ingevoerd";
        }
        setErrors(errors);
        return Object.keys(errors).length <= 0;
    }

    /**
     * Handle order submission
     * Validates form and shows confirmation popup with delivery details
     *
     * @param {Event} e - Click event
     */
    function order(e) {
        e.preventDefault();
        let orderPopupData = "";
        if (checkErrors()) {
            let title = "Bevestig bestelling";

            if (contact.delivery === 1) {
                // Delivery option selected
                if (!contact.zipcode) {
                    orderPopupData +=
                        "<p>De postcode is niet ingevuld. Is deze bestelling voor een nieuwbouw project?</p>";
                    title = "Postcode niet ingevuld?";
                }
                orderPopupData +=
                    "<p>Je order wordt verzonden naar onderstaand adres:<br/>" +
                    contact.address +
                    "<br/>" +
                    contact.zipcode +
                    " " +
                    contact.city +
                    "<br/><br/>Zijn bovenstaande gegevens juist?";
            } else {
                // Pickup option selected
                orderPopupData +=
                    "<p>U hebt gekozen voor afhalen bij de Mors.<br/>Wil je de bestelling bevestigen?";
            }
            setOrderPopupTitle(title);
            setOrderPopupData(orderPopupData);
            setOrderPopup(true);
        }
    }

    /**
     * Submit the order to the server
     * Handles success and error responses
     */
    function sendOrder() {
        api.post(saveOrder)
            .then((response) => {
                if (response.status === 201) {
                    setThanks(true); // Show success screen
                    setOrderPopup(false);
                } else {
                    setOrderPopup(false);
                    setErrors(response.data.errors);
                }
            })
            .catch((errors) => {
                setErrors(errors.response.data.errors);
            });
    }

    /**
     * Load contacts for a specific company
     *
     * @param {number} company_id - Company ID to load contacts for
     */
    function getContactsForCompany(company_id) {
        api.post(getContacts, { company_id: company_id }).then((response) => {
            setContacts(response.data);
        });
    }

    /**
     * Create a new product
     *
     * @param {Event} e - Click event
     */
    function createNewProduct(e) {
        e.preventDefault();
        Inertia.post(createNewProductLink, null, {headers: getHeaders()});
    }

    /**
     * Handle dropdown value changes with special handling for company selection
     * Automatically loads company address data when company changes
     *
     * @param {Object} value - New dropdown value
     * @param {string} property - Property being updated
     */
    async function updateDropdown(value, property = null) {
        if (
            property &&
            property === "company_id" &&
            contact.company_id !== value.company_id
        ) {
            // Company changed - load company address data
            await api.post("/api/company/address", {
                company_id: value.company_id,
            }).then(({ data }) => {
                value.delivery_company_name = data.companyname;
                value.delivery_date_preference = data.delivery_date_preference;
                value.address = data.locaddress;
                value.city = data.loccity;
                value.toelichting = data.locremark;
                value.zipcode = data.loczipcode;
                value.telephone = data.phone;
            });
            getContactsForCompany(value.company_id);
        }
        updateContact(value);
    }


    /**
     * Generate and display detailed product information
     * Creates HTML content showing product layers, dimensions, and finishing details
     *
     * @param {Event} e - Click event
     * @param {Object} product - Product to show details for
     */
    function setupProductData(e, product) {
        if (e) {
            e.preventDefault();
        }
        let informationstring =
            '<p style="line-height: 150%" class="productcontent">';

        // Product layer mapping
        let productsarray = {
            BU: "Buitenlaag",
            IS1: "Isolatie (1)",
            TL: "Kernplaat",
            IS2: "Isolatie (2)",
            BI: "Binnenlaag",
        };
        setProductDataTitle(product.product_name + " informatie");

        // Build layer information
        for (let key in productsarray) {
            if (
                productsarray.hasOwnProperty(key) &&
                product.products.hasOwnProperty(key) &&
                product.products[key]
            ) {
                informationstring += `<b>${productsarray[key]}:</b> ${product.products[key].name} ${
                    (key === "BU" || key === "BI") && product.properties["KL_" + key]
                        ? "(RAL" + product.properties["KL_" + key] + ")"
                        : ""
                }<br/>`;
            }
        }

        // Add dimensional information
        informationstring += `<br/><b>Dikte:</b> ${
            product.variations[0] ? product.variations[0].properties.DIKTE : 0
        }mm`;
        informationstring += `<br/><b>Positie kernplaat:</b> ${
            product.variations[0] ? product.variations[0].properties.TL_POS : 0
        }mm`;
        informationstring += `<br/><b>Sponning gewenst:</b> ${
            product.properties["toon_afwerking"] ? "Ja" : "Nee"
        }<br/>`;

        // Add finishing details if applicable
        if (product.properties.toon_afwerking) {
            if (product.properties.randen_afwerking_gelijk) {
                // Same finishing on all edges
                informationstring += `<b>Alle randen gelijke sponning/randafwerking:</b> Ja<br/>`;
                informationstring += `<b>Sponning/randafwerking:</b> ${
                    product.properties.sponning_rondom === "SP_BU" ? "Buiten" : "Binnen"
                }<br/>`;
                informationstring += `<b>Sponning/randafwerking breedte:</b> ${product.properties.sponning_rondom_breedte} mm<br/>`;
                informationstring += `<b>Sponning/randafwerking hoogte:</b> ${product.properties.sponning_rondom_hoogte} mm<br/>`;
            } else {
                // Individual edge finishing
                informationstring += `<b>Sponning/randafwerking links:</b> ${
                    product.properties.sponning_links === "SP_BU" ? "Buiten" : "Binnen"
                } - Breedte: ${product.properties[`${product.properties.sponning_links}_LI_BR`]} mm - Hoogte: ${
                    product.properties[`${product.properties.sponning_links}_LI_DI`]
                } mm<br/>`;
                informationstring += `<b>Sponning/randafwerking rechts:</b> ${
                    product.properties.sponning_rechts === "SP_BU" ? "Buiten" : "Binnen"
                } - Breedte: ${product.properties[`${product.properties.sponning_rechts}_RE_BR`]} mm - Hoogte: ${
                    product.properties[`${product.properties.sponning_rechts}_RE_DI`]
                } mm<br/>`;
                informationstring += `<b>Sponning/randafwerking boven:</b> ${
                    product.properties.sponning_boven === "SP_BU" ? "Buiten" : "Binnen"
                } - Breedte: ${product.properties[`${product.properties.sponning_boven}_BO_BR`]} mm - Hoogte: ${
                    product.properties[`${product.properties.sponning_boven}_BO_DI`]
                } mm<br/>`;
                informationstring += `<b>Sponning/randafwerking onder:</b> ${
                    product.properties.sponning_onder === "SP_BU" ? "Buiten" : "Binnen"
                } - Breedte: ${product.properties[`${product.properties.sponning_onder}_O_BR`]} mm - Hoogte: ${
                    product.properties[`${product.properties.sponning_onder}_O_DI`]
                } mm<br/>`;
            }
        }

        informationstring += "</p>";
        setProductDataContent(informationstring);
        setShowProductData(true);
    }

    return (
        <div
            className={`contentoptions ingaveoptions col-md-12 d-flex flex-column p-0 position-relative`}
        >
            {thanks ? (
                // Order success screen
                <div className={"row h-100"}>
                    <div className="col-12 shoppingpadding shoppingpaddingleft">
                        <Heading heading="De bestelling is verstuurd!" />
                        <p>
                            De bestelling is succesvol verzonden naar De Mors en
                            zal spoedig worden verwerkt.
                        </p>
                        <a
                            name={"Start nieuwe bestelling"}
                            href="/"
                            className="btn-orange"
                        >
                            Start nieuwe bestelling
                        </a>
                    </div>
                </div>
            ) : (
                <div className={"row h-100"}>

                    {/* Addd product list to the left side */}
                    <ProductList
                        products={products}
                        onCreateNewProduct={createNewProduct}
                        onDuplicateProduct={duplicateProduct}
                        onDeleteProduct={deleteProduct}
                        onShowProductData={setupProductData}
                    />

                    {/* Add order form to the right side */}
                    <OrderForm
                        products={products}
                        contact={contact}
                        updateContact={updateContact}
                        session={session}
                        updateSession={updateSession}
                        customers={customers}
                        contacts={contacts}
                        updateDropdown={updateDropdown}
                        onOrder={order}
                        errors={errors}
                    />
                </div>
            )}

            {/* Product deletion confirmation modal */}
            <Modal
                open={modalopen}
                title={"Verwijderen"}
                content={"Weet je zeker dat je dit product wilt verwijderen"}
                savetitle="Verwijder"
                buttonclass="btn-orange"
                canceltitle="Annuleer"
                savefunction={deleteProductDef}
                updateOpen={(e) => setModalOpen(e)}
            />

            {/* Order confirmation modal */}
            <Modal
                open={orderPopup}
                title={orderPopupTitle}
                datacontent={orderPopupData}
                savetitle="Bestel"
                buttonclass="btn-orange"
                canceltitle="Annuleer"
                savefunction={sendOrder}
                updateOpen={(e) => setOrderPopup(e)}
            />

            {/* Product details modal */}
            <Modal
                open={showProductData}
                title={productDataTitle}
                datacontent={productDataContent}
                hidecancel={true}
                savetitle="Sluit"
                buttonclass="btn-orange"
                savefunction={(e) => closeProductData(e)}
                updateOpen={(e) => closeProductData(e)}
            />
        </div>
    );
};

export default ShoppingList;
